import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { daysAgo, formatCompact, formatDuration, formatNumber, formatRelative, toDateInput } from '../format';
import type { LocationSummary, Note, OverviewFilterState, OverviewResponse } from '../types';
import { StatTile } from '../components/StatTile';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { DateRangeField, FilterPanel, SwitchField } from '../components/FilterPanel';
import { LocationSummaryPanel } from '../components/AiSummary';

export const DEFAULT_OVERVIEW_FILTERS: OverviewFilterState = {
  from: daysAgo(29),
  to: toDateInput(new Date()),
  locationIds: [],
  activeClientsOnly: true,
};

const PRESETS = [
  { label: 'Today', days: 0 },
  { label: '7d', days: 6 },
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
];

function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

function delta(current: number, previous: number) {
  const change = relativeChange(current, previous);
  return change === null ? null : { change, higherIsBetter: true };
}

type Report = 'calls' | 'appointments' | 'opportunities';

/**
 * Calls, appointments and opportunities together: the headline numbers, a
 * daily chart for each, and every sub-account side by side. Picking one
 * sub-account adds its saved notes and AI summary.
 */
export function Overview({
  filters,
  onFiltersChange,
  locations,
  dataVersion,
  showAi,
  canEditNotes,
  onOpen,
}: {
  filters: OverviewFilterState;
  onFiltersChange: (next: OverviewFilterState) => void;
  locations: LocationSummary[];
  dataVersion: number;
  showAi: boolean;
  canEditNotes: boolean;
  onOpen: (report: Report, locationId?: string) => void;
}) {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A filter change dims the page while it reloads; new data from a running
  // sync just swaps the numbers in, so the page does not flicker every few seconds.
  const lastFilters = useRef<OverviewFilterState | null>(null);
  useEffect(() => {
    let cancelled = false;
    const filtersChanged = lastFilters.current !== filters;
    lastFilters.current = filters;
    if (filtersChanged) setLoading(true);
    const timer = setTimeout(() => {
      api
        .overview(filters)
        .then((response) => {
          if (cancelled) return;
          setData(response);
          setError(null);
        })
        .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : String(cause)))
        .finally(() => !cancelled && setLoading(false));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters, dataVersion]);

  const today = toDateInput(new Date());
  const visibleLocations = filters.activeClientsOnly ? locations.filter((location) => location.isActiveClient) : locations;
  const selected = filters.locationIds.length === 1 ? filters.locationIds[0]! : null;
  const activeCount = (filters.locationIds.length ? 1 : 0) + (filters.activeClientsOnly ? 0 : 1);

  return (
    <div className="stack">
      <FilterPanel activeCount={activeCount} onReset={() => onFiltersChange(DEFAULT_OVERVIEW_FILTERS)}>
        <DateRangeField
          id="overview-from"
          label="Date range"
          from={filters.from}
          to={filters.to}
          max={today}
          presets={PRESETS.map((preset) => ({
            label: preset.label,
            from: preset.days === 0 ? today : daysAgo(preset.days),
            to: today,
          }))}
          onChange={(from, to) => onFiltersChange({ ...filters, from, to })}
        />
        <SwitchField
          id="overview-active"
          label="Clients"
          text="Active only"
          checked={filters.activeClientsOnly}
          onChange={(checked) => onFiltersChange({ ...filters, activeClientsOnly: checked, locationIds: [] })}
        />
        <div className="field">
          <label htmlFor="overview-location">Sub-account</label>
          <select
            id="overview-location"
            value={selected ?? ''}
            onChange={(event) => onFiltersChange({ ...filters, locationIds: event.target.value ? [event.target.value] : [] })}
          >
            <option value="">{filters.activeClientsOnly ? 'All active clients' : 'All sub-accounts'}</option>
            {visibleLocations.map((location) => (
              <option value={location.id} key={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      </FilterPanel>

      {error ? <div className="banner error">{error}</div> : null}

      {selected ? (
        <div className="grid grid-2">
          <LocationNotes locationId={selected} canEdit={canEditNotes} />
          {showAi ? <LocationSummaryPanel locationId={selected} from={filters.from} to={filters.to} /> : null}
        </div>
      ) : null}

      {!data ? (
        <p className="empty">{loading ? 'Loading overview…' : 'No data yet — run a sync.'}</p>
      ) : (
        <div className={loading ? 'stack reloading' : 'stack'}>
          <div className="grid grid-kpi">
            <StatTile
              label="Calls"
              value={formatCompact(data.calls.total)}
              hero
              delta={delta(data.calls.total, data.calls.previous.total)}
            />
            <StatTile
              label="Answered calls"
              value={formatCompact(data.calls.answered)}
              delta={delta(data.calls.answered, data.calls.previous.answered)}
              footnote={`${formatDuration(data.calls.talkSeconds)} talk time`}
            />
            <StatTile
              label="Appointments"
              value={formatCompact(data.appointments.total)}
              hero
              delta={delta(data.appointments.total, data.appointments.previous.total)}
            />
            <StatTile
              label="Showed"
              value={formatCompact(data.appointments.showed)}
              delta={delta(data.appointments.showed, data.appointments.previous.showed)}
              footnote={`${formatNumber(data.appointments.noShows)} no-shows · ${formatNumber(data.appointments.cancelled)} cancelled`}
            />
            <StatTile
              label="Opportunities"
              value={formatCompact(data.opportunities.total)}
              hero
              delta={delta(data.opportunities.total, data.opportunities.previous.total)}
            />
            <StatTile
              label="Won"
              value={formatCompact(data.opportunities.won)}
              delta={delta(data.opportunities.won, data.opportunities.previous.won)}
              footnote={`${formatNumber(Math.round(data.opportunities.wonValue))} won value · ${formatNumber(data.opportunities.open)} open`}
            />
          </div>

          <div className="grid grid-3">
            {(
              [
                ['calls', 'Calls per day', 'by when the call happened'],
                ['appointments', 'Appointments per day', 'by appointment start'],
                ['opportunities', 'Opportunities per day', 'by when the deal was created'],
              ] as [Report, string, string][]
            ).map(([key, title, basis]) => (
              <div className="card" key={key}>
                <div className="card-head">
                  <h2>{title}</h2>
                  <button type="button" className="btn btn-sm" onClick={() => onOpen(key, selected ?? undefined)}>
                    Open
                  </button>
                </div>
                <p className="card-sub">Counted {basis}.</p>
                <StackedColumnChart
                  data={data.timeseries.map((point) => ({ day: point.day, total: point[key], [key]: point[key] }))}
                  series={[{ key, label: title.replace(' per day', ''), color: 'var(--accent)' }]}
                  ariaLabel={title}
                />
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-head">
              <h2>By sub-account</h2>
              <span className="small muted">{data.byLocation.length} with activity</span>
            </div>
            <p className="card-sub">Click a sub-account to see it alone, with its notes.</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Sub-account</th>
                    <th className="num">Calls</th>
                    <th className="num">Answered</th>
                    <th className="num">Talk time</th>
                    <th className="num">Appointments</th>
                    <th className="num">Showed</th>
                    <th className="num">No-shows</th>
                    <th className="num">Opportunities</th>
                    <th className="num">Won</th>
                    <th className="num">Won value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byLocation.map((row) => (
                    <tr key={row.locationId}>
                      <td className="nowrap">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => onFiltersChange({ ...filters, locationIds: [row.locationId] })}
                        >
                          {row.locationName ?? row.locationId}
                        </button>
                      </td>
                      <td className="num">{formatNumber(row.calls)}</td>
                      <td className="num">{formatNumber(row.answered)}</td>
                      <td className="num">{formatDuration(row.talkSeconds)}</td>
                      <td className="num">{formatNumber(row.appointments)}</td>
                      <td className="num">{formatNumber(row.showed)}</td>
                      <td className="num">{formatNumber(row.noShows)}</td>
                      <td className="num">{formatNumber(row.opportunities)}</td>
                      <td className="num">{formatNumber(row.won)}</td>
                      <td className="num">{formatNumber(Math.round(row.wonValue))}</td>
                    </tr>
                  ))}
                  {data.byLocation.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="empty">
                        No calls, appointments or opportunities in this range.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** The notes saved for one sub-account, pinned first, with adding and editing for those allowed. */
function LocationNotes({ locationId, canEdit }: { locationId: string; canEdit: boolean }) {
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The note being written: `id` null for a new one. */
  const [draft, setDraft] = useState<{ id: string | null; title: string; body: string; pinned: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setNotes(null);
    setError(null);
    setDraft(null);
    api
      .notes(locationId)
      .then((response) => !cancelled && setNotes(response.notes))
      .catch((cause) => !cancelled && setError(cause instanceof ApiError ? cause.message : String(cause)));
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      if (!draft || !draft.body.trim()) return;
      const input = { title: draft.title.trim(), body: draft.body.trim(), pinned: draft.pinned };
      if (draft.id) {
        const { note } = await api.updateNote(locationId, draft.id, input);
        setNotes((current) => (current ?? []).map((item) => (item.id === note.id ? note : item)));
      } else {
        const { note } = await api.createNote(locationId, { ...input, title: input.title || undefined });
        setNotes((current) => [note, ...(current ?? [])]);
      }
      setDraft(null);
    });

  const togglePin = (note: Note) =>
    run(async () => {
      const { note: updated } = await api.updateNote(locationId, note.id, { pinned: !note.pinned });
      setNotes((current) => (current ?? []).map((item) => (item.id === updated.id ? updated : item)));
    });

  const remove = (note: Note) =>
    run(async () => {
      if (!window.confirm(`Delete the note “${note.title || note.body.slice(0, 40)}”?`)) return;
      await api.deleteNote(locationId, note.id);
      setNotes((current) => (current ?? []).filter((item) => item.id !== note.id));
    });

  const sorted = notes ? [...notes].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.updatedAt - a.updatedAt) : [];

  const editor = draft ? (
    <div className="note stack">
      <input
        type="text"
        aria-label="Note title"
        placeholder="Title (optional)"
        maxLength={200}
        value={draft.title}
        onChange={(event) => setDraft({ ...draft, title: event.target.value })}
      />
      <textarea
        aria-label="Note"
        rows={4}
        placeholder="What the team should know about this sub-account…"
        value={draft.body}
        autoFocus
        onChange={(event) => setDraft({ ...draft, body: event.target.value })}
      />
      <div className="row">
        <label className="switch">
          <input type="checkbox" checked={draft.pinned} onChange={(event) => setDraft({ ...draft, pinned: event.target.checked })} />
          Pinned
        </label>
        <span style={{ marginLeft: 'auto' }} />
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setDraft(null)}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm btn-primary" disabled={busy || !draft.body.trim()} onClick={save}>
          {busy ? 'Saving…' : draft.id ? 'Save' : 'Add note'}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="card">
      <div className="card-head">
        <h2>Notes</h2>
        <div className="row">
          {notes ? <span className="small muted">{notes.length}</span> : null}
          {canEdit && !draft ? (
            <button type="button" className="btn btn-sm" onClick={() => setDraft({ id: null, title: '', body: '', pinned: false })}>
              + Add note
            </button>
          ) : null}
        </div>
      </div>
      {error ? <div className="inline-result bad">{error}</div> : null}
      {!notes && !error ? <p className="empty">Loading…</p> : null}
      {notes && notes.length === 0 && !draft ? <p className="empty">No notes saved for this sub-account.</p> : null}
      <div className="stack notes-scroll">
        {draft && !draft.id ? editor : null}
        {sorted.map((note) =>
          draft?.id === note.id ? (
            <div key={note.id}>{editor}</div>
          ) : (
            <div className="note" key={note.id}>
              <div className="note-head">
                <span className="note-title">
                  {note.pinned ? '📌 ' : ''}
                  {note.title || 'Note'}
                </span>
                <span className="note-meta">
                  {note.author ? `${note.author} · ` : ''}
                  {formatRelative(note.updatedAt)}
                </span>
              </div>
              <div className="note-body">{note.body}</div>
              {canEdit ? (
                <div className="note-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={busy || Boolean(draft)}
                    onClick={() => setDraft({ id: note.id, title: note.title ?? '', body: note.body, pinned: note.pinned })}
                  >
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm" disabled={busy} onClick={() => togglePin(note)}>
                    {note.pinned ? 'Unpin' : 'Pin'}
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => remove(note)}>
                    Delete
                  </button>
                </div>
              ) : null}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
